import React, { useEffect, useState } from 'react';
import { Bell, Ticket, Gift, CheckCircle, ChevronLeft, ChevronRight, Check, Wallet, Settings, Mail, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Notifications() {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchNotifications = async () => {
      try {
        const response = await fetch('/api/notifications', {
          headers: {
            'Client-ID': 'lokanatya_client',
            'Client-Secret': 'super_secret_key_123',
            'Accept': 'application/json'
          }
        });
        const data = await response.json();
        if (data.status === 'success') {
          setNotifications(data.data);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, []);

  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    if (diffInSeconds < 60) return `Just now`;
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) return `${diffInDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-[#f8fafc] min-h-screen pb-20">
      
      {/* Header Container */}
      <div className="max-w-5xl mx-auto pt-6 md:pt-8 px-4 md:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3 md:gap-4">
            <button 
              onClick={() => navigate(-1)}
              className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-50 transition shrink-0 shadow-sm"
            >
              <ChevronLeft size={20} strokeWidth={2.5} className="w-4 h-4 md:w-5 md:h-5" />
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-extrabold text-[#0c0515]">Notifications</h1>
              <p className="text-gray-500 text-xs md:text-sm mt-0.5 font-medium">Stay updated with your bookings and offers.</p>
            </div>
          </div>
          
          <button className="hidden md:flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:text-blue-700 transition self-end sm:self-center">
            <Check size={16} strokeWidth={2.5} /> Mark all as read
          </button>
        </div>

        {/* Filters */}
        <div className="hidden md:flex items-center gap-3 overflow-x-auto pb-2 mb-6 scrollbar-hide">
          <button className="px-5 py-2 rounded-full bg-[#0d6efd] text-white font-bold text-sm shrink-0 shadow-sm shadow-blue-500/30">
            All
          </button>
          <button className="px-5 py-2 rounded-full bg-white border border-gray-200 text-gray-700 font-bold text-sm flex items-center gap-2 hover:bg-gray-50 shrink-0">
            <Ticket size={16} className="text-green-500" /> Bookings
          </button>
          <button className="px-5 py-2 rounded-full bg-white border border-gray-200 text-gray-700 font-bold text-sm flex items-center gap-2 hover:bg-gray-50 shrink-0">
            <Gift size={16} className="text-blue-500" /> Offers
          </button>
          <button className="px-5 py-2 rounded-full bg-white border border-gray-200 text-gray-700 font-bold text-sm flex items-center gap-2 hover:bg-gray-50 shrink-0">
            <Bell size={16} className="text-orange-500" /> Updates
          </button>
          <button className="px-5 py-2 rounded-full bg-white border border-gray-200 text-gray-700 font-bold text-sm flex items-center gap-2 hover:bg-gray-50 shrink-0">
            <Wallet size={16} className="text-purple-600" /> Payments
          </button>
        </div>

        {/* Notifications List */}
        <div className="bg-white rounded-[24px] shadow-sm border border-gray-100 overflow-hidden mb-6">
          <div className="divide-y divide-gray-100">
            {loading ? (
              <div className="p-12 text-center flex flex-col items-center">
                <div className="w-10 h-10 border-4 border-gray-100 border-t-blue-500 rounded-full animate-spin mb-4"></div>
                <div className="text-gray-500 font-medium">Loading notifications...</div>
              </div>
            ) : notifications.length > 0 ? (
              notifications.map((notif) => {
                const Icon = Bell; // Default icon
                return (
                  <div 
                    key={notif.id} 
                    className="p-4 md:p-6 flex flex-row items-start gap-3 md:gap-4 hover:bg-slate-50 transition cursor-pointer relative group"
                  >
                    {/* Icon container with unread indicator */}
                    <div className="relative shrink-0 mt-1 md:mt-0">
                      <div className="w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center bg-blue-50/80 text-blue-600 overflow-hidden shadow-inner border border-blue-100/50">
                        {notif.image ? (
                          <img src={notif.image} className="w-full h-full object-cover" alt="" />
                        ) : (
                          <Icon size={22} strokeWidth={2.5} className="md:w-6 md:h-6" />
                        )}
                      </div>
                      {/* Unread indicator dot attached to icon */}
                      <div className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-red-500 border-2 border-white shadow-sm"></div>
                    </div>

                    {/* Content wrapper */}
                    <div className="flex-1 min-w-0 flex flex-col pt-0.5">
                       <div className="flex justify-between items-start gap-2 mb-1">
                         <h3 className="text-[15px] md:text-base font-extrabold text-gray-900 leading-tight">{notif.title}</h3>
                         <span className="text-[11px] md:text-xs font-semibold text-gray-400 shrink-0 mt-0.5 bg-gray-50 px-2 py-0.5 rounded-full border border-gray-100">{timeAgo(notif.created_at)}</span>
                       </div>
                       <p className="text-[13px] md:text-sm font-medium text-gray-600 leading-relaxed pr-2 md:pr-8">{notif.message}</p>
                    </div>

                    {/* Chevron (Desktop only) */}
                    <div className="hidden md:flex items-center justify-center self-center shrink-0 w-8 h-8 rounded-full group-hover:bg-white group-hover:shadow-sm border border-transparent group-hover:border-gray-200 transition-all text-gray-300 group-hover:text-blue-500">
                      <ChevronRight size={18} strokeWidth={3} />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-16 text-center flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                  <Bell className="text-gray-300 w-8 h-8" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">You're all caught up!</h3>
                <p className="text-gray-500 text-sm">No new notifications at the moment.</p>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Settings Cards */}
        <div className="hidden md:flex bg-white rounded-[24px] shadow-sm border border-gray-100 p-8 flex-col md:flex-row gap-8 justify-between">
          
          {/* Push Notifications */}
          <div className="flex gap-4 flex-1">
            <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-500 flex items-center justify-center shrink-0">
              <Bell size={20} />
            </div>
            <div>
              <h4 className="text-sm font-extrabold text-gray-900 mb-1">Enable Push Notifications</h4>
              <p className="text-xs font-medium text-gray-500 mb-2 leading-relaxed">Get instant updates about bookings, offers and new shows.</p>
              <button className="text-sm font-bold text-blue-600 hover:text-blue-700">Enable Now</button>
            </div>
          </div>

          {/* Email Notifications */}
          <div className="flex gap-4 flex-1">
            <div className="w-12 h-12 rounded-full bg-green-50 text-green-500 flex items-center justify-center shrink-0">
              <Mail size={20} />
            </div>
            <div>
              <h4 className="text-sm font-extrabold text-gray-900 mb-1">Email Notifications</h4>
              <p className="text-xs font-medium text-gray-500 mb-2 leading-relaxed">Manage your email notification preferences.</p>
              <button className="text-sm font-bold text-blue-600 hover:text-blue-700">Manage</button>
            </div>
          </div>

          {/* Settings */}
          <div className="flex gap-4 flex-1">
            <div className="w-12 h-12 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
              <Settings size={20} />
            </div>
            <div>
              <h4 className="text-sm font-extrabold text-gray-900 mb-1">Notification Settings</h4>
              <p className="text-xs font-medium text-gray-500 mb-2 leading-relaxed">Customize what you want to receive.</p>
              <button className="text-sm font-bold text-blue-600 hover:text-blue-700">Settings</button>
            </div>
          </div>

        </div>

      </div>

    </div>
  );
}
